// レンダラのウォームアップ (spec/02 ロード戦略のプリロード)。
//
// - wasm: どのページでも (トップや 404 を含む)、load 後のアイドル時間に
//   取得・コンパイルしておく (277KB・一度きり)。
// - フォント: ルート変更のたびに「現在ページ + そこから遷移可能なページ」
//   が必要とするものだけを先読みする (routePrefetch.ts が articles.json と
//   ビルド時生成の fonts-usage.json から決定的に導出する)。全フォントの
//   一括先読みはしない。
//
// 取得はキャッシュ (HTTP / Service Worker / Worker のバイト列キャッシュ) を
// 素通しするため、キャッシュ済みなら実質何もしない。head の
// <link rel="preload"> は初回表示 (LCP) と帯域を奪い合った実績があるため
// 使わない (アイドル時に回すのが要点)。省データ設定・2G 相当の回線では
// フォントの先読みは行わない。

import { articlesIndex } from '../data';
import { fontFilesForRoute } from './routePrefetch';
import { warmupRenderer } from './workerClient';

function shouldPrefetchFonts(): boolean {
  interface Conn {
    saveData?: boolean;
    effectiveType?: string;
  }
  const conn = (navigator as Navigator & { connection?: Conn }).connection;
  if (conn?.saveData) {
    return false;
  }
  return !/(^|-)2g/.test(conn?.effectiveType ?? '');
}

function onIdle(run: () => void): void {
  const idle = () => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 5000 });
    } else {
      setTimeout(run, 1500);
    }
  };
  if (document.readyState === 'complete') {
    idle();
  } else {
    window.addEventListener('load', idle, { once: true });
  }
}

let wasmScheduled = false;

/** ページの load 後、アイドル時間に wasm の取得・コンパイルを 1 回だけ予約する。 */
export function scheduleRendererWarmup(): void {
  if (wasmScheduled) {
    return;
  }
  wasmScheduled = true;
  onIdle(() => {
    warmupRenderer([]).catch((e: unknown) => {
      console.warn('renderer warmup failed:', e);
    });
  });
}

type JsonMap = Record<string, string[]>;
let usagePromise: Promise<JsonMap> | null = null;
let manifestPromise: Promise<Record<string, string>> | null = null;

async function fetchJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(url);
    return res.ok ? ((await res.json()) as T) : fallback;
  } catch {
    return fallback;
  }
}

const prefetchedFonts = new Set<string>();

/**
 * ルートに応じたフォントの先読みをアイドル時間に予約する。
 * 同じフォントは 1 回しか要求しない (Worker 側にもキャッシュがある)。
 */
export function scheduleRouteFontPrefetch(path: string): void {
  if (!shouldPrefetchFonts()) {
    return;
  }
  onIdle(() => {
    void (async () => {
      usagePromise ??= fetchJson<JsonMap>('/fonts-usage.json', {});
      manifestPromise ??= fetchJson<Record<string, string>>('/fonts-manifest.json', {});
      const [usage, manifest] = await Promise.all([usagePromise, manifestPromise]);
      const urls: string[] = [];
      for (const file of fontFilesForRoute(articlesIndex, usage, path)) {
        if (prefetchedFonts.has(file)) {
          continue;
        }
        prefetchedFonts.add(file);
        const version = manifest[file];
        urls.push(version ? `/fonts/${file}?v=${version}` : `/fonts/${file}`);
      }
      if (urls.length > 0) {
        await warmupRenderer(urls);
      }
    })().catch((e: unknown) => {
      console.warn('font prefetch failed:', e);
    });
  });
}
